import React, { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import Menu from 'components/menu'
import Tooltip from 'components/tooltip'
import { CollapseAll, ExpandAll, Options, SearchLine as SearchIcon, Triangle } from 'components/icons'
import { PROJECT } from 'app-constants'
import SidebarMenuList, { SidebarMenuItem } from 'components/sidebar/menuItems'
import { SIDEBAR_ROW, SIDEBAR_ROW_IDLE, indentFor } from 'components/sidebar/surface'
import { belowButton, rowButtonProps } from 'components/sidebar/rowButton'
import { ROW_ACTIONS, ROW_ACTIONS_ROOM, RowAction } from 'components/sidebar/components/folders/rowParts'

const MENU_WIDTH = 200

/**
 * Where a site's name starts: the icon column of the rows at its level (a hub's badge, past the icon
 * slot's 6px padding), level with the hubs listed below it and its organisation's name above.
 */
const NAME_INSET = indentFor(0) + 6

/**
 * How far in a site's hubs start: their guide line runs down the middle of the icon column at the
 * site's level (the 18px column after NAME_INSET), and the rows begin just past it, so a hub's badge
 * lands one indent step (24px) in from the site's name.
 */
const HUBS_INSET = NAME_INSET + 9

/** An organisation's short name for a site's badge: the first word's first four letters, "ACME". */
export const organisationBadge = (name: string): string =>
  (name.trim().split(/\s+/)[0] ?? '')
    .replace(/[^\p{L}\p{N}]/gu, '')
    .slice(0, 4)
    .toUpperCase()

interface SiteHeadingProps {
  label: string
  /** The site's organisation, named in a small badge before the site's name; none for no badge. */
  organisation?: string
  collapsed: boolean
  /** Folds the site's hubs away, or brings them back. */
  onToggle: () => void
  /** Opens the home screen showing only this site's hubs, ready to type. */
  onSearch: () => void
  /** Every site is folded, so the all-sites action unfolds them rather than folds them. */
  allCollapsed: boolean
  /** Folds every site away, or, when every site is folded, unfolds them all. */
  onToggleAll: () => void
}

/** The action on every site: Collapse all sites while any is open, Expand all sites once all are folded. */
const allSitesAction = (allCollapsed: boolean) =>
  allCollapsed
    ? { label: 'Expand all sites', icon: <ExpandAll />, testId: 'expand-all' }
    : { label: 'Collapse all sites', icon: <CollapseAll />, testId: 'collapse-all' }

/**
 * A site's row in the hub list: its organisation's badge where the list mixes organisations, then the
 * site's name with a small chevron
 * tight after it, pointing down while open and right while folded, and the whole row folds away the
 * site's hubs below it (SiteHubs). Hovering also shows, at the row's end, a search of the site's hubs and a ⋯ with
 * Find hub in site and Collapse all sites (Expand all sites once every site is folded). The buttons sit beside the toggle, not inside it, so
 * neither is a button nested in another.
 */
const SiteHeading: React.FC<SiteHeadingProps> = ({
  label,
  organisation,
  collapsed,
  onToggle,
  onSearch,
  allCollapsed,
  onToggleAll
}) => {
  const [menu, setMenu] = useState<{ top: number; left: number; opener: HTMLElement } | null>(null)

  const close = (): void => {
    const opener = menu?.opener
    setMenu(null)
    if (opener?.isConnected) opener.focus({ preventScroll: true })
  }

  /** Closes the menu, then does `action`. */
  const choose = (action: () => void) => () => {
    close()
    action()
  }

  const allAction = allSitesAction(allCollapsed)
  // Two organisations can each have a London: the buttons' names say which, as the row's does.
  const ofOrganisation = organisation ? `, ${organisation}` : ''
  const items: SidebarMenuItem[] = [
    {
      testId: 'sidebar-site-find',
      icon: <SearchIcon />,
      label: `Find ${PROJECT} in site`,
      onSelect: choose(onSearch)
    },
    {
      testId: `sidebar-site-${allAction.testId}`,
      icon: allAction.icon,
      label: allAction.label,
      onSelect: choose(onToggleAll)
    }
  ]

  return (
    <div className="group relative">
      <div
        className={twMerge(
          SIDEBAR_ROW,
          SIDEBAR_ROW_IDLE,
          // The chevron tight after the name; room for the buttons while they show.
          'gap-0.5',
          menu ? 'pr-14' : ROW_ACTIONS_ROOM[2]
        )}
        style={{ paddingLeft: NAME_INSET }}
        aria-expanded={!collapsed}
        {...rowButtonProps(onToggle)}
      >
        {/* A fixed width, so every site's name starts at the same place whatever the letters: 42px,
            with its 6px margin and the row's 2px gap, puts the name level with its hubs' names. */}
        {organisation && (
          <Tooltip content={organisation} delay={300}>
            {/* Hidden from screen readers, which hear the organisation's full name after the site's. */}
            <span
              className="shrink-0 mr-1.5 inline-flex items-center justify-center w-[42px] h-4 rounded-[4px] bg-surface-strong text-body text-[10px] font-semibold leading-none tracking-[0.02em]"
              data-testid="sidebar-site-organisation"
              aria-hidden
            >
              {organisationBadge(organisation)}
            </span>
          </Tooltip>
        )}
        {/* min-w-0 so a long name shortens with "…" rather than pushing the chevron out of the sidebar. */}
        <Tooltip content={label} showOnlyIfTruncated delay={300}>
          <span className="truncate min-w-0 font-semibold">{label}</span>
        </Tooltip>
        {organisation && <span className="sr-only">, {organisation}</span>}
        <span className="shrink-0 flex text-muted [&>svg]:w-3 [&>svg]:h-3 [&_path[stroke]]:[stroke-width:2.4]">
          <Triangle type={collapsed ? 'right' : 'down'} />
        </span>
      </div>
      {/* Kept showing while the ⋯ menu is open. */}
      <span className={`${ROW_ACTIONS} ${menu ? 'visible' : ''}`}>
        <RowAction
          tooltip={`Search ${label} ${PROJECT}s`}
          label={`Search ${label} ${PROJECT}s${ofOrganisation}`}
          testId="sidebar-site-search"
          onClick={onSearch}
        >
          <SearchIcon />
        </RowAction>
        <RowAction
          tooltip="More actions"
          label={`More actions for ${label}${ofOrganisation}`}
          testId="sidebar-site-more"
          menuOpen={menu !== null}
          onClick={(button) => setMenu({ ...belowButton(button), opener: button })}
        >
          <Options />
        </RowAction>
      </span>
      {menu && (
        <Menu
          id="sidebar-site-menu"
          menuState={{ open: true, top: `${menu.top}px`, left: `${menu.left}px`, right: 'auto', bottom: 'auto' }}
          setMenuState={(state) => {
            if (!state.open) close()
          }}
          width={MENU_WIDTH}
          fixed
        >
          <SidebarMenuList label={label} items={items} onClose={close} />
        </Menu>
      )}
    </div>
  )
}

/**
 * The Sites label over the site rows, in the list's label style (`className`). Hovering it shows a
 * button at its end that folds every site, or unfolds them all once every site is folded: the same as
 * the all-sites entry in a site's ⋯.
 */
export const SitesLabel: React.FC<{ className: string; allCollapsed: boolean; onToggleAll: () => void }> = ({
  className,
  allCollapsed,
  onToggleAll
}) => {
  const { label, icon, testId } = allSitesAction(allCollapsed)
  return (
    <div className="group relative">
      <div className={twMerge(className, 'pr-8')}>Sites</div>
      {/* Level with the label's text, which sits at the bottom of its padded block. */}
      <span className={twMerge(ROW_ACTIONS, 'top-auto bottom-0 -translate-y-0.5')}>
        <RowAction tooltip={label} label={label} testId={`sidebar-sites-${testId}`} onClick={onToggleAll}>
          {icon}
        </RowAction>
      </span>
    </div>
  )
}

/**
 * A site's hubs, a step in under its row, with a hairline down their left, so they read as inside the
 * site, as a group's items do in a tree. The rows start past the line, and so do their hovers.
 */
export const SiteHubs: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border-l border-sidebar-line" style={{ marginLeft: HUBS_INSET }} data-testid="sidebar-site-hubs">
    {children}
  </div>
)

export default SiteHeading
